import { Link } from "@tanstack/react-router";
import { RevealOnScroll } from "./RevealOnScroll";
import { getToolLogoInfos } from "@/lib/tool-logos";

export type StackItem = { name: string; small: string; href?: string; ariaLabel?: string };

export function StackGrid({ items }: { items: StackItem[] }) {
  return (
    <RevealOnScroll>
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        {items.map((s) => {
          const logos = getToolLogoInfos(s.name);
          const baseClass =
            "group flex min-h-[120px] flex-col items-start gap-3 rounded-md border border-rule bg-white p-6 text-sm transition-colors";
          const linkClass = `${baseClass} cursor-pointer hover:border-cyan hover:shadow-[0_8px_24px_-16px_oklch(0.6_0.11_225/0.4)]`;
          const staticClass = `${baseClass} hover:border-rule`;
          const inner = (
            <>
              {logos.length > 0 && (
                <div className="flex h-8 w-[120px] items-center justify-start gap-2">
                  {logos.map((logo, index) => (
                    <img
                      key={`${s.name}-${index}`}
                      src={logo.src}
                      alt={`Logo: ${s.name}`}
                      title={`Logo: ${s.name}`}
                      width={52}
                      height={24}
                      loading="lazy"
                      decoding="async"
                      className="h-6 w-auto max-w-[52px] object-contain opacity-55 grayscale transition-[filter,opacity] duration-200 ease-out group-hover:opacity-100 group-hover:grayscale-0"
                    />
                  ))}
                </div>
              )}
              <div className="min-w-0 w-full">
                <div className="font-medium text-ink [overflow-wrap:anywhere] hyphens-auto">
                  {s.name}
                  {s.href && (
                    <span
                      aria-hidden="true"
                      className="ml-1 inline-block text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-cyan-deep"
                    >
                      →
                    </span>
                  )}
                </div>
                <div
                  className="mt-1 font-mono text-xs uppercase tracking-wider text-ink-3 [overflow-wrap:anywhere] hyphens-auto"
                >
                  {s.small}
                </div>
              </div>
            </>
          );
          if (s.href) {
            return (
              <Link key={s.name} to={s.href} aria-label={s.ariaLabel ?? s.name} className={linkClass}>
                {inner}
              </Link>
            );
          }
          return (
            <div key={s.name} className={staticClass}>
              {inner}
            </div>
          );
        })}
      </div>
    </RevealOnScroll>
  );
}
